import type { ReactNode } from 'react';
import { useBreakpoint } from '../../hooks';
import { CdAppHeader, type CdAppHeaderProps } from '../../organisms/CdAppHeader';
import { CdMobileNavDrawer } from '../../organisms/CdMobileNavDrawer';
import { CdSideNav } from '../../organisms/CdSideNav';
import { CdSiteFooter } from '../../organisms/CdSiteFooter';
import type { LinkComponent, NavItemConfig } from '../../types';
import { cx } from '../../utils';
import styles from './CdAppShellTemplate.module.scss';

export type NavMode = 'expanded' | 'rail' | 'closed';

export interface CdAppShellTemplateProps {
  navItems: NavItemConfig[];
  isActive: (href: string) => boolean;
  linkComponent?: LinkComponent;
  /** Desktop (≥ lg) navigation state. Below lg the nav is always a drawer. */
  navMode: NavMode;
  onNavModeChange: (mode: NavMode) => void;
  mobileNavOpen: boolean;
  onMobileNavOpenChange: (open: boolean) => void;
  header: Omit<CdAppHeaderProps, 'onOpenNav' | 'showNavToggle'>;
  footerText: string;
  /** Overlays rendered at the root (notifications panel, filter panel…). */
  panels?: ReactNode;
  skipLinkLabel?: string;
  children: ReactNode;
}

/**
 * Authenticated application layout: navy SideNav + sticky header + main content.
 * ≥ lg: SideNav expanded / rail / closed (controlled). < lg: hamburger opens MobileNavDrawer.
 */
export const CdAppShellTemplate = ({
  navItems,
  isActive,
  linkComponent,
  navMode,
  onNavModeChange,
  mobileNavOpen,
  onMobileNavOpenChange,
  header,
  footerText,
  panels,
  skipLinkLabel = 'Skip to main content',
  children,
}: CdAppShellTemplateProps) => {
  const isDesktop = useBreakpoint('lg');
  const showDesktopNav = isDesktop && navMode !== 'closed';

  return (
    <div className={cx(styles.shell, showDesktopNav && styles[navMode])}>
      <a href="#main-content" className={styles.skipLink}>
        {skipLinkLabel}
      </a>

      {showDesktopNav && (
        <div className={styles.navColumn}>
          <CdSideNav
            items={navItems}
            isActive={isActive}
            linkComponent={linkComponent}
            mode={navMode === 'rail' ? 'rail' : 'expanded'}
            onToggleMode={() => onNavModeChange(navMode === 'rail' ? 'expanded' : 'rail')}
            footerText={footerText}
          />
        </div>
      )}

      <div className={styles.mainColumn}>
        <CdAppHeader
          {...header}
          showNavToggle={!showDesktopNav}
          onOpenNav={() => (isDesktop ? onNavModeChange('expanded') : onMobileNavOpenChange(true))}
        />
        <main id="main-content" className={styles.main} tabIndex={-1}>
          {children}
        </main>
        {!isDesktop && <CdSiteFooter variant="compact" copyright={footerText} links={[]} />}
      </div>

      {!isDesktop && (
        <CdMobileNavDrawer
          open={mobileNavOpen}
          onClose={() => onMobileNavOpenChange(false)}
          items={navItems}
          isActive={isActive}
          linkComponent={linkComponent}
          footerText={footerText}
        />
      )}
      {panels}
    </div>
  );
};
